import React, { useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import {
  CandidateSummaryGap,
  CandidateSummaryPayload,
  CandidateSummaryRecommendation,
} from '../models/candidate-summary.model';
import {
  ASSESSMENT_SECTIONS,
  recommendationClientGuidance,
  recommendationLabel,
} from '../utils/candidate-summary-labels';
import { normalizeAssessmentGaps } from '../utils/candidate-summary-gaps';
import { CandidateSummaryGapsSection } from './CandidateSummaryGapsSection';

const { TextArea } = Input;

export interface CandidateSummaryEditValues {
  recommendation: CandidateSummaryRecommendation;
  headline: string;
  executiveSummary: string;
  strengthsText: string;
  interviewFocusText: string;
}

interface CandidateSummaryEditFormProps {
  payload: CandidateSummaryPayload;
  onChange: (payload: CandidateSummaryPayload) => void;
}

function linesToList(value?: string): string[] {
  return (value ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function listToLines(items?: string[]): string {
  return items?.join('\n') ?? '';
}

export function payloadToEditValues(payload: CandidateSummaryPayload): CandidateSummaryEditValues {
  return {
    recommendation: payload.recommendation,
    headline: payload.headline ?? '',
    executiveSummary: payload.executiveSummary ?? '',
    strengthsText: listToLines(payload.strengths),
    interviewFocusText: listToLines(payload.interviewFocus),
  };
}

export function editValuesToPayload(
  values: CandidateSummaryEditValues,
  source: CandidateSummaryPayload
): CandidateSummaryPayload {
  return {
    ...source,
    recommendation: values.recommendation,
    headline: values.headline.trim(),
    executiveSummary: values.executiveSummary.trim(),
    strengths: linesToList(values.strengthsText),
    gaps: normalizeAssessmentGaps(source),
    gapsOrRisks: undefined,
    interviewFocus: linesToList(values.interviewFocusText),
  };
}

const labelClass = 'text-left block mb-2 text-sm font-medium text-gray-600';

export const CandidateSummaryEditForm: React.FC<CandidateSummaryEditFormProps> = ({
  payload,
  onChange,
}) => {
  const [form] = Form.useForm<CandidateSummaryEditValues>();

  useEffect(() => {
    form.setFieldsValue(payloadToEditValues(payload));
  }, [form, payload]);

  const handleValuesChange = (
    _: Partial<CandidateSummaryEditValues>,
    allValues: CandidateSummaryEditValues
  ) => {
    onChange(editValuesToPayload(allValues, payload));
  };

  const handleGapsChange = (gaps: CandidateSummaryGap[]) => {
    onChange({
      ...payload,
      gaps,
      gapsOrRisks: undefined,
    });
  };

  return (
    <div className="space-y-6">
      <Form
        form={form}
        layout="vertical"
        className="text-left"
        onValuesChange={handleValuesChange}
        initialValues={payloadToEditValues(payload)}
      >
        <Form.Item
          label={<span className={labelClass}>Overall recommendation</span>}
          name="recommendation"
          rules={[{ required: true, message: 'Select a recommendation.' }]}
        >
          <Select
            className="rounded-lg"
            options={(
              Object.keys(recommendationLabel) as CandidateSummaryRecommendation[]
            ).map((value) => ({
              value,
              label: `${recommendationLabel[value]} — ${recommendationClientGuidance[value]}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          label={<span className={labelClass}>Headline</span>}
          name="headline"
          rules={[{ required: true, message: 'Enter a headline.' }]}
        >
          <Input className="rounded-lg" maxLength={300} />
        </Form.Item>

        <Form.Item
          label={<span className={labelClass}>{ASSESSMENT_SECTIONS.recruiterSummary}</span>}
          name="executiveSummary"
          rules={[{ required: true, message: 'Enter the recruiter summary.' }]}
        >
          <TextArea className="rounded-lg" rows={5} />
        </Form.Item>

        <Form.Item
          label={<span className={labelClass}>{ASSESSMENT_SECTIONS.keyStrengths}</span>}
          name="strengthsText"
          extra="One item per line."
        >
          <TextArea className="rounded-lg" rows={4} />
        </Form.Item>

        <Form.Item
          label={<span className={labelClass}>{ASSESSMENT_SECTIONS.interviewTalkingPoints}</span>}
          name="interviewFocusText"
          extra="One item per line."
        >
          <TextArea className="rounded-lg" rows={4} />
        </Form.Item>
      </Form>

      <CandidateSummaryGapsSection
        payload={payload}
        editing
        onChange={handleGapsChange}
      />
    </div>
  );
};
